// The admin panel of an admin seat (R50, R94; ui-map-interface.md §13,
// R1766), as pure markup.
//
// One list of rows, two hosts. The desktop draws it as the `#admin` panel
// over the map, in the frame and the head of the OPTIONS popup (R1774),
// which stays open while the tester plays. The telephone
// draws it as one page of the ☸ OPTIONS menu: the whole screen, the head
// OPTIONS › DEV, ‹ BACK to the root and ✕ CLOSE on the caps of the tab bar,
// like every other page of the menu.
//
// Every button carries `data-act="adm"` and its operation in `data-op`, so
// the overlay's one dispatcher sends both hosts to the same admin ops. The
// three buttons that throw the world away take two presses (R696): the
// overlay's gate gives each its label and its armed state.

import { overlay as S } from '../../gen/strings';
import { esc } from '../esc';
import { ADVANCE_STEPS, CLOCK_LADDER, MAP_CLOCK_LADDER } from '../overlay/tables';
import { dockIcon } from '../overlay/thumbdock';
import { head, note, row } from './kit';

/** The three two-press operations of the panel. */
export type DevArmOp = 'roll' | 'reseed' | 'restart';

/** What the admin panel shows, as the overlay reads it at one render. */
export interface DevPanelView {
  /** The world clock and the map clock, as multipliers of wall time. */
  timescale: number;
  tactical: number;
  /** The fog is off for this seat. */
  reveal: boolean;
  /** The survey filter is off for this connection (R2825). */
  veilOff: boolean;
  /** Every cache of the season stands on the map for this seat (R2825). */
  caches: boolean;
  /** The autopilot flies this empire. */
  autopilot: boolean;
  /** The label and the armed state of each two-press button. The label
   *  holds both faces of the press (`armFaces`, R2069). */
  arm: Readonly<Record<DevArmOp, { label: string; armed: boolean }>>;
}

/** One button of a row. A label is markup from the strings table. */
interface DevButton {
  label: string;
  op: string;
  n?: number;
  title?: string;
  /** The value in force: the current clock rung, an active toggle. */
  on?: boolean;
  danger?: boolean;
  armed?: boolean;
  /** A two-press button (`data-arm`). */
  twoPress?: boolean;
}

/** One row of the panel: a name and its buttons. `ladder` marks a choice
 *  of one value (a clock), which the telephone draws as a segmented
 *  choice. */
interface DevRow {
  name: string;
  title?: string;
  buttons: DevButton[];
  ladder?: boolean;
}

/** The rows of the panel, in the order of the desktop card. */
function devRows(v: DevPanelView): DevRow[] {
  const ladder = (op: string, rungs: readonly number[], scale: number, name: string, title: string): DevRow => ({
    name, title, ladder: true,
    buttons: rungs.map((n) => ({ label: String(n), op, n, on: n === scale, title: S.admin_scale_title({ n }) })),
  });
  const armed = (op: DevArmOp, danger: boolean, title?: string): DevButton => ({
    label: v.arm[op].label, op, danger, armed: v.arm[op].armed, twoPress: true, title,
  });
  return [
    { name: S.admin_row_economy, buttons: [{ label: S.admin_grant, op: 'grant' }, { label: S.admin_rush, op: 'rush' }] },
    {
      name: S.admin_row_progress,
      buttons: [{ label: S.admin_maxbase, op: 'maxbase' },
        { label: S.admin_maxfleet, op: 'maxfleet', title: S.admin_maxfleet_title }],
    },
    ladder('timescale', CLOCK_LADDER, v.timescale, S.admin_world_clock({ scale: v.timescale }), S.admin_world_clock_title),
    ladder('tacticalscale', MAP_CLOCK_LADDER, v.tactical, S.admin_map_clock({ scale: v.tactical }), S.admin_map_clock_title),
    // Advance time (R183): the world clock jumps, and nothing runs, so
    // the title says so on every button.
    {
      name: S.admin_advance, title: S.admin_advance_title,
      buttons: ADVANCE_STEPS.map((sec, i) => ({
        label: S.advance_steps[i], op: 'advance', n: sec,
        title: S.admin_advance_step_title({ step: S.advance_steps[i] }),
      })),
    },
    {
      name: S.admin_row_seaquake,
      buttons: [{ label: S.admin_stage1, op: 'quake', n: 1 }, { label: S.admin_stage2, op: 'quake', n: 2 },
        { label: S.admin_stage3, op: 'quake', n: 3 },
        // Quake 4 (R2096): the end of the drain, so the card opens at once.
        { label: S.admin_drain_end, op: 'quake', n: 4, title: S.admin_drain_end_title },
        armed('roll', false)],
    },
    // TOGGLE VEIL lifts the survey filter of this connection, and TOGGLE
    // CACHES shows every cache of the season (R2825). Both are server ops.
    {
      name: S.admin_row_view,
      buttons: [{ label: S.admin_fog, op: 'reveal', on: v.reveal },
        { label: S.admin_veil, op: 'veil', on: v.veilOff, title: S.admin_veil_title },
        { label: S.admin_caches, op: 'caches', on: v.caches, title: S.admin_caches_title }],
    },
    {
      name: S.admin_row_empire,
      buttons: [{ label: S.admin_autopilot, op: 'autopilot', on: v.autopilot },
        // RELOAD WARDROOM (R1141): the next join loads the carried
        // wardroom into this empire again.
        { label: S.admin_reload_wardroom, op: 'reloadwardroom', title: S.admin_reload_wardroom_title }],
    },
    {
      // RESEED (R118) keeps accounts, captains and Honour, so it is the
      // milder of the two danger buttons and stands before RESTART.
      name: S.admin_row_server,
      buttons: [{ label: S.admin_saverestart, op: 'saverestart' },
        armed('reseed', true, S.admin_reseed_title), armed('restart', true)],
    },
  ];
}

/** The press attributes of one button. */
function attrs(b: DevButton): string {
  return ` data-act="adm" data-op="${b.op}"${b.n === undefined ? '' : ` data-n="${b.n}"`}`
    + `${b.twoPress ? ' data-arm="1"' : ''}${b.title ? ` title="${esc(b.title)}"` : ''}`;
}

/**
 * The frame of the desktop panel `#admin` (R1774): the popup of the OPTIONS
 * menu with no dimmed map and no rail, so the tester keeps the map in play
 * under it. The head and the rows stand in slots the overlay writes on its
 * own, so a press keeps the scroll of the rows. The foot holds the
 * statistics line where the OPTIONS popup holds the session block; the
 * overlay writes it into `data-devstats` once a second.
 */
export function devDeskShell(): string {
  return `<div class="o-pop o-devpop" role="dialog" aria-label="${esc(S.admin_title)}">`
    + '<div class="o-slot" data-slot="head"></div>'
    + '<div class="o-scroll" data-slot="body"></div>'
    + '<div class="o-devfoot" data-devstats></div></div>';
}

/** The head of the desktop panel: the DEV icon and the title, the world
 *  line, and the ✕ of the OPTIONS head, which closes the panel. */
export function devDeskHead(world: string): string {
  return head({ title: S.admin_title, glyph: dockIcon('dev', 14), world, close: { act: 'admintoggle' } });
}

/**
 * The rows of the panel (R1766, R1774): the line under the title as a
 * note, then one settings row of the kit per row. On the telephone page
 * (`wrap`) the buttons of a row stand on a line of their own; the desktop
 * panel stands them to the right of the name. A clock is a segmented
 * choice; a danger button is red and its armed state is the kit's armed
 * button.
 */
export function devPageHtml(v: DevPanelView, wrap = true): string {
  const btn = (b: DevButton): string => {
    // The kit button (R2011): danger, its armed state, the lit state of a
    // switch that is on, or primary.
    let cls = 'ch-btn';
    if (b.armed) cls += ' bad armed';
    else if (b.danger) cls += ' bad';
    else cls += b.on ? ' on' : ' pri';
    return `<button type="button" class="${cls}"${b.on ? ' aria-pressed="true"' : ''}${attrs(b)}>${b.label}</button>`;
  };
  const seg = (b: DevButton): string => `<button type="button" class="o-segb${b.on ? ' on' : ''}" role="radio"`
    + ` aria-checked="${!!b.on}"${attrs(b)}>${b.label}</button>`;
  return note(S.admin_sub) + devRows(v).map((r) => row({
    name: r.name, what: r.title, wrap,
    control: r.ladder
      ? `<span class="o-devseg" role="radiogroup" aria-label="${esc(r.name)}">${r.buttons.map(seg).join('')}</span>`
      : `<span class="o-devbtns">${r.buttons.map(btn).join('')}</span>`,
  })).join('');
}
